import * as THREE from "three";
import {
  WallShapeType,
  WallCornerKey,
  WallCornerDescriptor,
  WallCornerMap,
} from "../types/floorplannerTypes";

export const RECT_CORNER_ORDER: WallCornerKey[] = [
  "topLeft",
  "topRight",
  "bottomRight",
  "bottomLeft",
];

export type CornerReferenceSource = "named" | "inferred";

export type CornerReference = {
  key: WallCornerKey;
  point?: THREE.Vector2;
  indices: number[];
  source: CornerReferenceSource;
};

const ensureCornerMap = (shape: WallShapeType): WallCornerMap => {
  if (!shape.corners) {
    shape.corners = {};
  }
  return shape.corners;
};

/**
 * Provide a reference to the requested corner. Uses explicit corner descriptors
 * when available, otherwise falls back to the legacy path index assumptions.
 */
export const getCornerReference = (
  shape: WallShapeType,
  key: WallCornerKey,
): CornerReference => {
  const explicitCorner = shape.corners?.[key];
  if (explicitCorner) {
    return {
      key,
      point: explicitCorner.point,
      indices: explicitCorner.pathIndices ? [...explicitCorner.pathIndices] : [],
      source: "named",
    };
  }

  return inferCornerReference(shape, key);
};

/**
 * Update the logical corner descriptor and, by default, the underlying path vertices.
 * When the corner is not yet registered, a new descriptor is created.
 */
export const setCornerPoint = (
  shape: WallShapeType,
  key: WallCornerKey,
  point: THREE.Vector2,
  options?: {
    pathIndices?: number[];
    mutatesPaths?: boolean;
  },
): WallCornerDescriptor => {
  const cornerMap = ensureCornerMap(shape);
  const mutatesPaths = options?.mutatesPaths ?? true;
  const indices =
    options?.pathIndices ??
    cornerMap[key]?.pathIndices ??
    inferCornerReference(shape, key).indices;

  const targetPoint =
    cornerMap[key]?.point ?? new THREE.Vector2(point.x, point.y);

  targetPoint.copy(point);

  const descriptor: WallCornerDescriptor = {
    key,
    point: targetPoint,
    pathIndices: indices.length > 0 ? [...indices] : undefined,
  };

  cornerMap[key] = descriptor;

  if (mutatesPaths) {
    indices.forEach((index) => {
      if (index >= 0 && index < shape.paths.length) {
        shape.paths[index].copy(point);
      }
    });
  }

  return descriptor;
};

/**
 * Infer a reference for the requested corner based solely on legacy path indexing.
 * This mirrors the current assumptions in trimming/rendering logic so that
 * introducing corner descriptors does not change behaviour until explicitly used.
 */
export const inferCornerReference = (
  shape: WallShapeType,
  key: WallCornerKey,
): CornerReference => {
  const indices = inferCornerIndicesFromPaths(shape, key);
  const index = indices.length > 0 ? indices[0] : -1;
  const point =
    index >= 0 && index < shape.paths.length ? shape.paths[index] : undefined;

  return {
    key,
    point,
    indices,
    source: "inferred",
  };
};

export const getCornerPoint = (
  shape: WallShapeType,
  key: WallCornerKey,
  options?: { clone?: boolean },
): THREE.Vector2 | undefined => {
  const { clone = false } = options ?? {};
  const reference = getCornerReference(shape, key);
  const index = reference.indices.length > 0 ? reference.indices[0] : -1;
  const fallback =
    index >= 0 && index < shape.paths.length ? shape.paths[index] : undefined;
  const point = reference.point ?? fallback;
  if (!point) {
    return undefined;
  }
  return clone ? point.clone() : point;
};

export const getCornerIndices = (
  shape: WallShapeType,
  key: WallCornerKey,
): number[] => {
  return getCornerReference(shape, key).indices;
};

export const getPrimaryCornerIndex = (
  shape: WallShapeType,
  key: WallCornerKey,
): number => {
  const reference = getCornerReference(shape, key);
  return reference.indices.length > 0 ? reference.indices[0] : -1;
};

export const updatePathPoint = (
  shape: WallShapeType,
  index: number,
  point: THREE.Vector2,
): void => {
  if (!shape.paths || index < 0 || index >= shape.paths.length) {
    return;
  }

  shape.paths[index].copy(point);

  if (shape.corners) {
    Object.values(shape.corners).forEach(descriptor => {
      if (!descriptor || !descriptor.pathIndices) {
        return;
      }
      if (descriptor.pathIndices.includes(index)) {
        descriptor.point.copy(point);
      }
    });
  }
};

const inferCornerIndicesFromPaths = (
  shape: WallShapeType,
  key: WallCornerKey,
): number[] => {
  const { paths } = shape;

  if (!paths || paths.length === 0) {
    return [];
  }

  if (shape.wall.lineForm === "arc") {
    return inferCornerIndicesForArc(paths.length, key);
  }

  if (shape.lineSide === "left" || shape.lineSide === "right") {
    return inferCornerIndicesForOutline(paths.length, key);
  }

  return inferCornerIndicesForPolygon(paths.length, key);
};

const inferCornerIndicesForArc = (
  pathsCount: number,
  key: WallCornerKey,
): number[] => {
  if (pathsCount === 0) {
    return [];
  }

  switch (key) {
    case "topLeft":
      return [0];
    case "topRight":
      return [pathsCount - 1];
    case "bottomRight":
      return [Math.floor(pathsCount / 2)];
    case "bottomLeft":
      return [Math.floor(pathsCount / 2) - 1];
    default:
      return [];
  }
};

const inferCornerIndicesForOutline = (
  pathsCount: number,
  key: WallCornerKey,
): number[] => {
  if (pathsCount < 2) {
    return [];
  }

  switch (key) {
    case "topLeft":
    case "bottomLeft":
      return [0];
    case "topRight":
    case "bottomRight":
      return [1];
    default:
      return [];
  }
};

const inferCornerIndicesForPolygon = (
  pathsCount: number,
  key: WallCornerKey,
): number[] => {
  if (pathsCount < 4) {
    return [];
  }

  switch (key) {
    case "topLeft":
      return [0];
    case "topRight":
      return [1];
    case "bottomRight":
      return [2];
    case "bottomLeft":
      return [3];
    default:
      // Center corners (topCenter/bottomCenter) are no longer supported in the type system
      // With edge connections, walls are always 4-point rectangles
      return [];
  }
};

type CornerAssignments = Partial<Record<WallCornerKey, number | number[]>>;

const normaliseIndices = (
  shape: WallShapeType,
  value: number | number[] | undefined,
): number[] => {
  if (typeof value === "undefined") {
    return [];
  }
  const indices = Array.isArray(value) ? value : [value];
  return indices.filter(
    (index) => index >= 0 && index < shape.paths.length && Number.isInteger(index),
  );
};

const assignCornerFromIndices = (
  shape: WallShapeType,
  key: WallCornerKey,
  indices: number[],
) => {
  if (indices.length === 0) {
    return;
  }
  const primaryIndex = indices[0];
  const point = shape.paths[primaryIndex];
  if (!point) {
    return;
  }
  setCornerPoint(shape, key, point, {
    pathIndices: indices,
    mutatesPaths: false,
  });
};

/**
 * Populate the shape with default corner descriptors based on existing legacy ordering.
 * Optional additional assignments can be provided to associate further indices
 * (e.g., centre points inserted for multi-joint fills).
 */
export const populateDefaultCorners = (
  shape: WallShapeType,
  options?: {
    additionalAssignments?: CornerAssignments;
  },
): void => {
  // With edge connections, walls are always 4-point rectangles
  // Legacy data with center corners will be filtered during deserialization
  const assignments: CornerAssignments = { ...(options?.additionalAssignments || {}) };

  if (shape.corners) {
    Object.values(shape.corners).forEach(descriptor => {
      if (!descriptor || !descriptor.pathIndices || descriptor.pathIndices.length === 0) {
        return;
      }
      if (!assignments[descriptor.key]) {
        assignments[descriptor.key] = [...descriptor.pathIndices];
      }
    });
  }

  const assign = (key: WallCornerKey, index: number | number[]) => {
    if (assignments[key]) {
      // Allow explicit assignments to override defaults
      return;
    }
    const validIndices = normaliseIndices(shape, index);
    if (validIndices.length > 0) {
      assignments[key] = validIndices;
    }
  };

  if (shape.wall.lineForm === "arc") {
    const count = shape.paths.length;
    if (count >= 2) {
      assign("topLeft", 0);
      assign("topRight", count - 1);
    }
    if (count >= 4) {
      assign("bottomRight", Math.floor(count / 2));
      assign("bottomLeft", Math.floor(count / 2) - 1);
    }
  } else if (shape.lineSide === "left" || shape.lineSide === "right") {
    assign("topLeft", 0);
    assign("bottomLeft", 0);
    assign("topRight", 1);
    assign("bottomRight", 1);
  } else if (shape.paths.length >= 4) {
    assign("topLeft", 0);
    assign("topRight", 1);
    assign("bottomRight", 2);
    assign("bottomLeft", 3);
  }

  Object.entries(assignments).forEach(([key, value]) => {
    const indices = normaliseIndices(shape, value as number | number[] | undefined);
    if (indices.length > 0) {
      assignCornerFromIndices(shape, key as WallCornerKey, indices);
    }
  });
};
